import React from 'react';

//Styles
import styles from './AssignPollDialog.module.css';

// Services
import { getFrequencyPoll, getDaysFrequencyPoll, getFrequencies } from '../../../services/getPollAttribute';
import { getEnvironmentApiUrl } from '../../../services/General';
import { areFrequenciesEqual } from '../../../services/Poll';

// Hooks
import { Dispatch, SetStateAction, useState, useEffect } from 'react';
import useHttp from '../../../hooks/useHttp';
import { useToast } from '../../../hooks/useToast';

// Components
import CircleSelector from '../../UI/CircleSelector/CircleSelector';
import CancelConfirmButtons from '../../UI/CancelConfirmButtons/CancelConfirmButtons';
import ErrorSpinnerWrapper from '../../UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import CasesCheckList from '../../UI/CasesCheckList/CasesCheckList';
import Modal from '../../UI/Modal/Modal';
import FrequencySelector from '../../UI/FrequencySelector/FrequencySelector';

// Interfaces
import { FrequencyType, Poll, PollFrequency } from '../../../interfaces/Poll';

// Language
import { useTranslation } from 'react-i18next';

const AssignPollDialog: React.FC<{
    assign: boolean
    poll: Poll
    showModal: Dispatch<SetStateAction<boolean>>,
}> = ({ assign, showModal, poll }) => {

    const { t } = useTranslation();

    const toast = useToast();

    const [selectedDays, setSelectedDays] = useState<string>(getFrequencyPoll(poll));
    const [selectedCases, setSelectedCases] = useState<{ case_id: number, patient_id: number }[]>([]);
    const [casesAssigned, setCasesAssigned] = useState<number[]>([]);
    const [showFrequencySelector, setShowFrequencySelector] = useState<boolean>(false);

    const DEFAULT_FREQ: PollFrequency = {
        frecuency: poll.frecuency,
        monthly_frequency_day: poll.monthly_frequency_day,
        semanal_frequency: poll.semanal_frequency,
        frequency_duration: poll.frequency_duration,
    };

    const [temporaryFreq, setTemporaryFreq] = useState<PollFrequency>(DEFAULT_FREQ);

    const { sendRequest: sendlinkRequest } = useHttp();
    const { isLoading, error, sendRequest: sendPatientsAssignedToPoll } = useHttp<number[]>();

    useEffect(() => {
        sendPatientsAssignedToPoll({
            url: getEnvironmentApiUrl() + `inquiry/${poll.id}/cases`,
        }, (data) => setCasesAssigned(data));
    }, [poll.id]);

    const addRemoveSelectedCase = (caseID: number, patientID: number) => {
        setSelectedCases(oldPatientsSelected => {
            const newPatientsSelected = [...oldPatientsSelected];
            return newPatientsSelected.some(p => p.case_id === caseID) ?
                newPatientsSelected.filter(p => p.case_id !== caseID)
                :
                [...newPatientsSelected, { case_id: caseID, patient_id: patientID }];
        });
    };

    const handleConfirm = () => {

        const freqToSend: PollFrequency = selectedDays === 'custom' ? temporaryFreq : { ...DEFAULT_FREQ, frecuency: getDaysFrequencyPoll(selectedDays) };
        const hasChangedFrequency = !areFrequenciesEqual(DEFAULT_FREQ, freqToSend);

        if (selectedCases.length > 0) sendlinkRequest({
            url: getEnvironmentApiUrl() + (assign ? 'patient_inquiry/multiple' : `inquiry/${poll.id}/unlink_cases`),
            method: 'POST',
            body: assign ? selectedCases.map(p => {
                return {
                    patient_id: p.patient_id,
                    case_id: p.case_id,
                    inquiry_id: poll.id,
                    ...hasChangedFrequency ? freqToSend : DEFAULT_FREQ,
                    modifed_frequency: hasChangedFrequency,
                    modifed_reminder: false,
                    reminder_time: poll.reminder_time,
                    start_date: new Date().toISOString(),
                    end_date: null,
                    auto_call_enabled: poll.auto_call_enabled,
                };
            }) : selectedCases.map(p => p.case_id),
        }, () => toast.success(<p>{t('the_poll')} <b>{poll.name}</b> {assign ? t('has_been_assigned_to') : t('has_been_unassigned_from')} {selectedCases.length} {t('patients').toLocaleLowerCase()}</p>)
            , () => toast.error(<p>{t('the_poll')} <b>{poll.name}</b> {assign ? t('could_not_be_assigned') : t('could_not_be_unassigned')}</p>)
        );
        showModal(false);
    };

    const handleChangeFrequency = (frequency: FrequencyType) => () => {
        setSelectedDays(frequency);
        if (frequency === 'custom') setShowFrequencySelector(true);
    };

    const handleChangeCustomFreq = (freq: PollFrequency) => {
        setTemporaryFreq(freq);
    };

    const selectedCasesIds = selectedCases.map(p => p.case_id);

    return (
        <div className='dialog'>
            <h1 className='dialogTitle'>{assign ? t('assign_poll') : t('unassign_poll')}</h1>
            <div className={styles.selectPatientContainer}>
                <div className={styles.patientsChecklist}>
                    <ErrorSpinnerWrapper
                        error={error}
                        loading={isLoading}
                    >
                        <CasesCheckList
                            label={t('select_patient')}
                            casesLinked={assign ? [...casesAssigned, ...selectedCasesIds] : selectedCasesIds}
                            casesBlocked={assign ? casesAssigned : undefined}
                            casesToShow={!assign ? casesAssigned : undefined}
                            handleClickCheckbox={addRemoveSelectedCase}
                            searchbar
                        />
                    </ErrorSpinnerWrapper>
                </div>
            </div>
            {assign && <div>
                <h3 className={styles.subtitle}>{t('periodicity')}: </h3>
                <div className={styles.freqOptionsContainer}>
                    {getFrequencies().map(frequency =>
                        <CircleSelector key={frequency}
                            text={t(frequency)}
                            active={selectedDays === frequency}
                            handleClick={handleChangeFrequency(frequency)} />
                    )}
                </div>
            </div>}
            <CancelConfirmButtons
                onCancel={() => showModal(false)}
                onConfirm={handleConfirm}
            />
            <Modal
                show={showFrequencySelector}
                setShowModal={setShowFrequencySelector}
                secondModal
            >
                <FrequencySelector
                    frequency={{
                        frecuency: poll?.frecuency,
                        monthly_frequency_day: poll?.monthly_frequency_day,
                        semanal_frequency: poll?.semanal_frequency,
                        frequency_duration: poll?.frequency_duration,
                    }}
                    startEndDate
                    onCancel={() => setShowFrequencySelector(false)}
                    onConfirm={handleChangeCustomFreq}
                    setShow={setShowFrequencySelector}
                />
            </Modal>
        </div>
    );
};
export default AssignPollDialog;